import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Route, Router } from '@angular/router';
import { th } from 'date-fns/locale';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { Manager } from 'src/app/model/Manager';
import { Roles } from 'src/app/model/Roles';
import { Gender } from 'src/app/model/gender';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { BreadcrumbItem } from 'src/app/modules/shared/components/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-liste-user',
  templateUrl: './liste-user.component.html',
  styleUrls: ['./liste-user.component.css'],
})
export class ListeUserComponent implements OnInit {
  user!: any;
  showDetails: boolean = false;
  showModal: boolean = false;
  ligneDetails: any;
  createUserForm!: FormGroup;
  genders = Gender.List;
  roles!: Roles[];
  active!: boolean;
  update: boolean = false;
  notification: boolean = false;
  error: boolean = false;
  valid: boolean = false;
  showActiveModal: boolean = false;
  showDesactiveModal: boolean = false;
  activated: boolean = false;
  desactivated: boolean = false;
  errorStatus: boolean = false;
  deleted: boolean = false;
  deleteError: boolean = false;

  searchCtrl!: FormControl<any>;
  searchText$ = new BehaviorSubject<string>('');

  // Pagination properties
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  paginatedManagers$ = new BehaviorSubject<any[]>([]);
  totalElements = 0;
  totalPages = 0;
  currentPage = 0;
  pageSize = 10;
  isLoading = false;
  errorr: string | null = null;
  itemsPerPage: number = 15;

  private pageChange$ = new BehaviorSubject<{page: number, size: number}>({
    page: 0,
    size: 10
  });

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeSearch();
    this.setupPaginatedData();
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.page.subscribe((event: PageEvent) => {
        this.onPageChange(event);
      });
    }
  }

  private initializeSearch(): void {
    this.searchCtrl = this.formBuilder.control('');
    this.searchCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), startWith(''))
      .subscribe((searchTerm) => {
        this.searchText$.next(searchTerm || '');
        this.resetToFirstPage();
      });
  }

  private setupPaginatedData(): void {
    combineLatest([
      this.searchText$,
      this.pageChange$
    ])
      .pipe(
        switchMap(([searchText, pageInfo]) => {
          this.isLoading = true;
          this.errorr = null;
          // Si recherche, effectuer le filtrage côté client sur toutes les données
          if (searchText && searchText.trim()) {
            return this.performClientSideSearch(searchText, pageInfo);
          } else {
            // Sinon, utiliser la pagination côté serveur
            return this.performServerSidePagination(pageInfo);
          }
        }),
        tap(() => (this.isLoading = false))
      )
      .subscribe({
        next: (response) => {
          this.paginatedManagers$.next(response.content);
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.currentPage = response.number;

          // Mise à jour du paginator
          if (this.paginator) {
            this.paginator.length = this.totalElements;
            this.paginator.pageIndex = this.currentPage;
            this.paginator.pageSize = this.pageSize;
          }
        },
        error: (err) => {
          console.error('Erreur lors du chargement des données:', err);
          this.errorr = 'Erreur lors du chargement des données';
          this.isLoading = false;
        }
      });
  }

  private performServerSidePagination(pageInfo: {page: number, size: number}): Observable<any> {
    return this.authService
      .getAllManagersPaged(pageInfo.page, pageInfo.size)
      .pipe(
        catchError((err) => {
          console.error('Erreur lors du chargement des données:', err);
          return of({
            content: [],
            totalElements: 0,
            totalPages: 0,
            number: 0,
            size: pageInfo.size,
          });
        })
      );
  }

  private performClientSideSearch(searchText: string, pageInfo: {page: number, size: number}): Observable<any> {
    return this.authService
      .getAllManagersPaged(0, 1000)
      .pipe(
        map((response) => {
          const searchFields = ['firstname', 'lastname', 'email', 'iuf', 'profil'];
          const searchTerm = searchText.toLowerCase();

          const filteredData = response.content.filter((manager: any) => {
          // Recherche par champs classiques
          const matchesBasicFields = searchFields.some((field) =>
            manager[field]?.toLowerCase().includes(searchTerm)
          );
          // Recherche par profil (nom du rôle)
          const profileName = this.profileId(manager.roles);
          const matchesProfile = profileName.toLowerCase().includes(searchTerm);

          return matchesBasicFields || matchesProfile;
        });

          const startIndex = pageInfo.page * pageInfo.size;
          const endIndex = startIndex + pageInfo.size;
          const paginatedData = filteredData.slice(startIndex, endIndex);

          return {
            content: paginatedData,
            totalElements: filteredData.length,
            totalPages: Math.ceil(filteredData.length / pageInfo.size),
            number: pageInfo.page,
            size: pageInfo.size
          };
        }),
        catchError((err) => {
          console.error('Erreur lors de la recherche:', err);
          return of({
            content: [],
            totalElements: 0,
            totalPages: 0,
            number: 0,
            size: pageInfo.size,
          });
        })
      );
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.pageChange$.next({
      page: this.currentPage,
      size: this.pageSize
    });
  }

  private resetToFirstPage(): void {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.pageChange$.next({
      page: 0,
      size: this.pageSize
    });
  }

  // Méthodes utilitaires pour le template
  get displayedManagers$(): Observable<any[]> {
    return this.paginatedManagers$.asObservable();
  }

  profileId(roles: any): string {
    const role =
      roles[0] != undefined && roles[0].id != undefined
        ? roles[0].id
        : roles[0];

    if (role == 1) {
      return 'Administrateur';
    } else if (role == 2) {
      return 'Gestionnaire FCC';
    } else if (role == 3) {
      return 'Eleve';
    } else if (role == 4) {
      return 'Admin FCC';
    } else if (role == 6) {
      return 'Mentor';
    } else if (role == 7) {
      return 'Gestionnaire STEMD';
    } else if (role == 8) {
      return 'Gestionnaire AIPEN';
    } else if (role == 9) {
      return 'Admin AIPEN';
    } else if (role == 10) {
      return 'Admin COM ';
    } else if (role == 11) {
      return 'Admin SEV';
    } else if (role == 12) {
      return 'Communication';
    } else if (role == 13) {
      return 'Suivi Evaluation';
    } else if (role == 14) {
      return 'Admin Finance';
    } else if (role == 15) {
      return 'Finance';
    } else if (role == 16) {
      return 'Etudiant STEMD';
    } else if (role == 17) {
      return 'Chef Etablissement';
    } else if (role == 18) {
      return 'Programme Board';
    } else {
      return 'Apprenant';
    }
  }

  toggleModalDetails(row: Manager) {
    this.ligneDetails = row;
    const roles = this.ligneDetails.roles;
    this.active =
      this.ligneDetails.active === null || this.ligneDetails.active === true
        ? true
        : false;

    const role =
      roles[0] != undefined && roles[0].id != undefined
        ? roles[0].id
        : roles[0];
    this.createUserForm = this.formBuilder.group({
      email: [
        { value: this.ligneDetails.email, disabled: true },
        Validators.required,
      ],
      lastname: [this.ligneDetails.lastname, Validators.required],
      firstname: [this.ligneDetails.firstname, Validators.required],
      selectedGender: [this.ligneDetails.gender.id, Validators.required],
      selectedProfil: [role, Validators.required],
    });
    this.showDetails = true;
  }

  closeModalDetails() {
    this.update = false;
    this.showDetails = false;
  }

  showSuppModal(row: any) {
    this.ligneDetails = row;
    this.showModal = true;
    // console.log(row.roles.at(0).id);
  }

  closeSuppModal() {
    this.showModal = false;
  }

  showFormUpdate() {
    this.update = true;
  }
  hideFormUpdate() {
    this.update = false;
  }

  onCheckedChange(event: any) {
    if (event.target.checked) {
      this.showActiveModal = true;
    } else {
      this.showDesactiveModal = true;
    }
  }
  closeActiveModal() {
    this.showActiveModal = false;
    this.active = false;
  }

  closeDesactiveModal() {
    this.showDesactiveModal = false;
    this.active = true;
  }

  updateUser() {
    if (this.createUserForm.valid) {
      const formValue = this.createUserForm.value;

      const beneficiary = new Manager(
        formValue['email'],
        formValue['lastname'],
        formValue['firstname'],
        { id: formValue['selectedGender'] },
        { id: 4 },
        { id: formValue['selectedProfil'] }
      );

      this.authService
        .updateUser(this.ligneDetails.iuf, beneficiary)
        .subscribe({
          next: (reponse) => {
            if (reponse) {
              if (reponse.status == 200) {
                this.notification = true;

                setTimeout(() => {
                  this.notification = false;
                  this.update = false;
                  this.showDetails = false;
                  window.location.reload();
                }, 4000);
              } else if (reponse.status == 400) {
                setTimeout(() => {
                  //this.errorCountExist = false;
                  this.error = false;

                  //
                }, 4000);
              } else if (reponse.status == 500) {
                this.error = true;

                setTimeout(() => {
                  this.error = false;

                  window.location.reload();
                }, 4000);
              }
            } else {
              this.error = true;

              setTimeout(() => {
                this.error = false;

                window.location.reload();
              }, 4000);
            }
          },
          error: (err) => {
            this.error = true;
            // //console.log(err)
            setTimeout(() => {
              this.error = false;
            }, 4000);

            window.location.reload();
          },
        });
    } else {
      this.valid = true;

      setTimeout(() => {
        this.valid = false;
      }, 4000);
    }
  }

  enbaleAccount() {
    this.authService.enableAccount(this.ligneDetails.email).subscribe({
      next: (reponse) => {
        if (reponse) {
          if (reponse.status == 200) {
            this.activated = true;
            this.showActiveModal = false;

            setTimeout(() => {
              this.activated = false;
              this.showDetails = false;
              window.location.reload();
            }, 4000);
          } else if (reponse.status == 400) {
            this.errorStatus = true;
            setTimeout(() => {
              this.errorStatus = false;
            }, 4000);
          }
        } else {
          this.errorStatus = true;

          setTimeout(() => {
            this.errorStatus = false;
          }, 4000);
        }
      },
      error: (err) => {
        this.errorStatus = true;

        setTimeout(() => {
          this.errorStatus = false;
        }, 4000);
      },
    });
  }

  disableAccount() {
    this.authService.disableAccount(this.ligneDetails.email).subscribe({
      next: (reponse) => {
        if (reponse) {
          if (reponse.status == 200) {
            this.desactivated = true;
            this.showDesactiveModal = false;

            setTimeout(() => {
              this.desactivated = false;
              this.showDetails = false;
              window.location.reload();
            }, 4000);
          } else if (reponse.status == 400) {
            this.errorStatus = true;

            setTimeout(() => {
              //this.errorCountExist = false;
              this.errorStatus = false;
            }, 4000);
          }
        } else {
          this.errorStatus = true;

          setTimeout(() => {
            this.errorStatus = false;

            window.location.reload();
          }, 4000);
        }
      },
      error: (err) => {
        this.errorStatus = true;
        // //console.log(err)
        setTimeout(() => {
          this.errorStatus = false;
        }, 4000);

        window.location.reload();
      },
    });
  }

  ddeleteUser() {
    this.authService.deleteUser(this.ligneDetails.iuf).subscribe({
      next: (reponse) => {
        if (reponse) {
          this.showModal = false;

          if (reponse.status == 200) {
            window.scrollTo(0, 0);
            this.deleted = true;

            setTimeout(() => {
              this.deleted = false;
              window.location.reload();
            }, 4000);
          } else if (reponse.status == 400) {
            this.deleteError = true;

            setTimeout(() => {
              this.deleteError = false;
            }, 4000);
          }
        } else {
          this.deleteError = true;

          setTimeout(() => {
            this.deleteError = false;

            window.location.reload();
          }, 4000);
        }
      },
      error: (err) => {
        this.deleteError = true;

        setTimeout(() => {
          this.deleteError = false;
        }, 4000);
      },
    });
  }

  breadcrumbItems: BreadcrumbItem[] = [
    { label: 'UTILISATEURS' },
    { label: 'GESTION DES UTILISATEURS', url: '/dashboard/users/list' },
  ];
}
